import React, { FC, useEffect, useState } from 'react';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableHead from '@mui/material/TableHead';
import MuiTableRow from '@mui/material/TableRow';
import {
    FilterOperatorType,
    OnChangeOptionsType,
    RowActionPropsType,
    SelectRowType,
    TableColumnType,
    TablePropsOptionsType,
    TableRowType
} from 'src/types/table';
import MuiTableSortLabel from '@mui/material/TableSortLabel';
import TextField, { TextFieldValueType } from '../TextField';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { isUndefined } from 'src/utils/isDefined';
import Select, { OptionType, SelectValueType } from '../Select';
import Checkbox from '../Checkbox';
import {
    getHeaderLabel,
    getIdOrArrayFilter,
    getStringFromArrayFilter,
    getStringOrArrayFilter,
    parseTableDirection
} from './functions';
import NumberFilter from './NumberFilter';
import DateRange, { PropValueType } from '../DateRange';
import MuiButton from '@mui/material/Button';
import MuiTooltip from '@mui/material/Tooltip';
import translate from '@translate';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';
import { SxProps, Theme } from '@mui/material/styles';
import { InfoIcon } from '../SkladonIcon/Icons';
import colorOptionFce, { isBoolLikeSelectOption } from 'src/utils/colorOptionFce';

const FilterSpacer = styled('div')({
    height: '47px',
    // backgroundColor: '#F6F9FF',
    backgroundColor: colors.background_B0
});

const InfoIconStyled = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
    marginTop: '-5px',
    cursor: 'pointer',
    verticalAlign: 'middle',
    '& svg': {
        width: '16px',
        height: '16px',
        verticalAlign: 'middle',
    }
});

interface TableHeaderPropsType {
    options: TablePropsOptionsType;
    rowIdKey: string;
    selectRowIdKey?: string;
    rows: TableRowType[];
    visilbeColumns: TableColumnType[];
    loading?: boolean;
    onChangeOptions?: OnChangeOptionsType;
    selectOptions?: { [key: string]: ConstantsCodeType[] };
    columsLabelPrefix?: string;
    disableFilter?: boolean;
    selectedRows?: SelectRowType[];
    setSelectedRows?: (selectedRows: SelectRowType[]) => void;
    showToggleColumns?: boolean;
    openToggleColumns: () => void;
    rowActions?: RowActionPropsType[];
    showClearFilters?: boolean;
    optionStyleFce?: (option: OptionType) => SxProps<Theme> | null;
}

const TableHeader: FC<TableHeaderPropsType> = (props) => {
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);

    useEffect(() => {
        if (props.setSelectedRows) {
            setAllRowsSelected(
                !props.rows.find(
                    (rowItem) => !props.selectedRows?.includes(String(rowItem[props.selectRowIdKey || props.rowIdKey]))
                )
            );
        }
    }, [props.rows, props.rowIdKey, props.selectedRows]);

    const handleChangeSort = (column: TableColumnType) => {
        if (!column.disableSort && props.onChangeOptions) {
            props.onChangeOptions({
                ...props.options,
                sort: {
                    column: column.key,
                    direction: (
                        props.options.sort.column === column.key ? props.options.sort.direction === 'DESC' : false
                    )
                        ? 'ASC'
                        : 'DESC',
                    ...(column.numericSort ? { numericOrAlphabetic: true } : {}),
                    ...(column.key === props.rowIdKey ? { createIdNumber: true } : {}),
                }
            });
        }
    };

    const handleChangeFilter = (
        column: TableColumnType,
        value: SelectValueType | SelectValueType[] | TextFieldValueType,
        operator: FilterOperatorType
    ) => {
        let filters = props.options.filters || [];

        filters = filters.filter((item) => item.column !== column.key);

        if (Array.isArray(value)) {
            if (value.length) {
                const newValueItems = value.map((item) => String(item));
                filters.push({
                    column: column.key,
                    operator,
                    value: newValueItems
                });
            }
        } else {
            if (!isUndefined(value)) {
                filters.push({
                    column: column.key,
                    operator,
                    value: String(value)
                });
            }
        }

        if (props.onChangeOptions) {
            props.onChangeOptions({
                ...props.options,
                filters,
                page: 1
            });
        }
    };

    const handleSelectAllRows = (value: number | boolean) => {
        if (props.setSelectedRows) {
            if (value) {
                props.setSelectedRows(
                    props.rows.map((rowItem) => String(rowItem[props.selectRowIdKey || props.rowIdKey]))
                );
            } else {
                props.setSelectedRows([]);
            }
        }
    };

    const handleClearFilters = () => {
        if (props.onChangeOptions) {
            props.onChangeOptions({ ...props.options, filters: [] });
        }
    };

    const resolveOptionStyleFce = (column: TableColumnType) => {
        if (typeof column.optionStyleFce === 'function') {
            return column.optionStyleFce;
        }
        if (column.optionStyleFce && props.optionStyleFce) {
            return props.optionStyleFce;
        }
        if (column.selectOption && isBoolLikeSelectOption(column.selectOption)) {
            return colorOptionFce;
        }
        return undefined;
    };

    const renderFilterInput = (column: TableColumnType, label: string) => {
        if (column.disableFilter) {
            return <FilterSpacer />;
        }
        const filterItem = (props.options.filters || []).find((filterItem) => filterItem.column === column.key);

        switch (column.type) {
            case 'multi-select':
            case 'select':
                if (props.selectOptions && column.selectOption && props.selectOptions[column.selectOption]) {
                    return (
                        <Select
                            value={filterItem?.value || []}
                            options={props.selectOptions[column.selectOption].map((item) => ({
                                label: item.label,
                                value: String(item.value),
                                color: item.color
                            }))}
                            fullWidth
                            portal
                            onChange={(value) => handleChangeFilter(column, value, 'in')}
                            limitTags={1}
                            multiple
                            name={label}
                            optionsExpandWidth
                            disableVirtualized
                            optionStyleFce={resolveOptionStyleFce(column)}
                        />
                    );
                }

                return null;
            case 'number':
                return (
                    <TextField
                        value={getStringFromArrayFilter(filterItem?.value) || ''}
                        fullWidth
                        onChange={(value) => {
                            const newOptions = getStringOrArrayFilter(String(value || ''));
                            handleChangeFilter(column, newOptions.value || undefined, newOptions.operator);
                        }}
                        // type="numberLocale"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        name={label}
                        clearValueUndefined
                    />
                );
            case 'id':
                return (
                    <TextField
                        value={getStringFromArrayFilter(filterItem?.value) || ''}
                        fullWidth
                        onChange={(value) => {
                            const newOptions = getIdOrArrayFilter(String(value || ''));
                            handleChangeFilter(column, newOptions.value || undefined, newOptions.operator);
                        }}
                        name={label}
                        clearValueUndefined
                    />
                );
            case 'number-filter':
                return (
                    <NumberFilter
                        operator={filterItem?.operator || 'like'}
                        value={filterItem?.value || ''}
                        onChange={(value, operator: FilterOperatorType) => handleChangeFilter(column, value, operator)}
                        name={label}
                    />
                );
            case 'dateTime':
            case 'date':
                return (
                    <DateRange
                        value={Array.isArray(filterItem?.value) ? filterItem?.value : undefined}
                        onChange={(value: PropValueType) => handleChangeFilter(column, value, 'between')}
                        fullWidth
                    />
                );

            default:
                return (
                    <TextField
                        value={getStringFromArrayFilter(filterItem?.value) || ''}
                        fullWidth
                        onChange={(value) => {
                            const newOptions = getStringOrArrayFilter(String(value || ''));
                            handleChangeFilter(column, newOptions.value || undefined, newOptions.operator);
                        }}
                        name={label}
                        clearValueUndefined
                    />
                );
        }
    };

    return (
        <MuiTableHead>
            <MuiTableRow>
                {props.setSelectedRows && (
                    <MuiTableCell className="row-select">
                        <Checkbox
                            value={allRowsSelected}
                            onChange={(value: number | boolean) => handleSelectAllRows(value)}
                        />
                        {!props.disableFilter && <div className="MuiFormControl-root" />}
                    </MuiTableCell>
                )}

                {props.visilbeColumns.map((column) => {
                    const label = getHeaderLabel(column, props.columsLabelPrefix);
                    const labelTranslate = translate(label);
                    const tooltipText = column.tooltip ? translate(column.tooltip) : null;

                    return (
                        <MuiTableCell key={column.key} sx={column.customStyleHeader}>
                            <MuiTableSortLabel
                                active={props.options.sort.column === column.key}
                                direction={parseTableDirection(
                                    props.options.sort.column === column.key ? props.options.sort.direction : 'DESC'
                                )}
                                onClick={() => handleChangeSort(column)}
                                title={labelTranslate}
                                disabled={column.disableSort}
                            >
                                {labelTranslate}
                                {tooltipText && (
                                    <MuiTooltip title={tooltipText}>
                                        <InfoIconStyled>
                                            <InfoIcon />
                                        </InfoIconStyled>
                                    </MuiTooltip>
                                )}
                            </MuiTableSortLabel>
                            {!props.disableFilter && renderFilterInput(column, label)}
                        </MuiTableCell>
                    );
                })}
                {(props.rowActions || props.showClearFilters) && (
                    <MuiTableCell className="row-action">
                        {props.rowActions && props.showToggleColumns ? (
                            <MuiButton onClick={props.openToggleColumns} data-testid="table-show-columns-btn">
                                <MuiTooltip title={translate('table.showColumns')}>
                                    <ViewColumnIcon />
                                </MuiTooltip>
                            </MuiButton>
                        ) : null}
                        {!props.disableFilter && !!props.options.filters?.length && (
                            <MuiButton onClick={handleClearFilters} className="table-clear-filters">
                                <MuiTooltip title={translate('table.clearFilters')}>
                                    <HighlightOffIcon />
                                </MuiTooltip>
                            </MuiButton>
                        )}
                    </MuiTableCell>
                )}
            </MuiTableRow>
        </MuiTableHead>
    );
};

export default TableHeader;
